import { Button } from '@chiroup/components/Button';
import { ConfirmModal } from '@chiroup/components/ConfirmModal';
import { createDecimal } from '@chiroup/core/functions/createDecimal';
import {
  CrossoverRepairCandidate,
  CrossoverRepairClinic,
  CrossoverRepairPreview,
  CrossoverRepairScope,
} from '@chiroup/core/types/CrossoverRepair.type';
import { useState } from 'react';
import {
  applyCrossover,
  listCrossoverClinics,
  previewCrossover,
  scanCrossovers,
} from '../../../services/crossoverRepair.service';
import Header from '../../layout/Header';

type RepairRow = CrossoverRepairCandidate & {
  preview?: CrossoverRepairPreview;
  selected: boolean;
  result?: string;
  repaired?: boolean;
  needsRefresh?: boolean;
};

const canSelect = (row: RepairRow) =>
  !row.repaired && !row.needsRefresh && row.preview?.eligible !== false;

const formatMoney = (value: string) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(Number(value));

const errorMessage = (error: unknown): string => {
  const requestError = error as { response?: { data?: { message?: string } }; message?: string };
  return (
    requestError.response?.data?.message ||
    requestError.message ||
    'The request could not be completed.'
  );
};

const CrossoverRepair = () => {
  const [clinicId, setClinicId] = useState('');
  const [patientId, setPatientId] = useState('');
  const [scope, setScope] = useState<CrossoverRepairScope>();
  const [rows, setRows] = useState<RepairRow[]>([]);
  const [clinics, setClinics] = useState<CrossoverRepairClinic[]>();
  const [nextInvoiceId, setNextInvoiceId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [hasScanned, setHasScanned] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const selected = rows.filter(row => row.selected && canSelect(row));
  const increase = selected
    .reduce((total, row) => total.plus(createDecimal(row.billedAmount)), createDecimal(0))
    .toFixed(2);
  const inputClass =
    'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-dark-gray-600 dark:bg-dark-gray-800';

  const clearResults = () => {
    setScope(undefined);
    setRows([]);
    setClinics(undefined);
    setNextInvoiceId(null);
    setHasScanned(false);
    setError('');
  };

  const scan = async (more = false, selectedClinicId?: number) => {
    if (!more && selectedClinicId === undefined && !clinicId.trim()) {
      if (patientId.trim()) {
        setError('Enter a clinic ID to filter by patient.');
        return;
      }
      setBusy(true);
      clearResults();
      try {
        const response = await listCrossoverClinics({ fn: 'clinics' });
        setClinics(response.clinics);
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setBusy(false);
      }
      return;
    }
    const requestedScope = more
      ? scope
      : {
          clinicId: selectedClinicId ?? Number(clinicId),
          ...(patientId.trim() ? { patientId: patientId.trim() } : {}),
        };
    if (
      !requestedScope ||
      !Number.isSafeInteger(requestedScope.clinicId) ||
      requestedScope.clinicId <= 0
    ) {
      setError('Enter a valid clinic ID.');
      return;
    }
    setBusy(true);
    setError('');
    if (!more) clearResults();
    try {
      const response = await scanCrossovers({
        ...requestedScope,
        fn: 'scan',
        ...(more && nextInvoiceId ? { afterInvoiceId: nextInvoiceId } : {}),
      });
      setScope(requestedScope);
      setRows(previous => [
        ...(more ? previous : []),
        ...response.candidates.map(candidate => ({ ...candidate, selected: false })),
      ]);
      setNextInvoiceId(response.nextInvoiceId);
      setHasScanned(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const preview = async (invoiceId?: number) => {
    if (!scope) return;
    const pending = invoiceId
      ? rows.filter(row => row.invoiceId === invoiceId)
      : rows.filter(row => !row.repaired);
    setBusy(true);
    setError('');
    try {
      for (const candidate of pending) {
        try {
          const result = await previewCrossover({
            ...scope,
            fn: 'preview',
            invoiceId: candidate.invoiceId,
          });
          setRows(previous =>
            previous.map(row =>
              row.invoiceId === candidate.invoiceId
                ? {
                    ...row,
                    preview: result,
                    selected: row.selected && result.eligible,
                    needsRefresh: false,
                    result: undefined,
                  }
                : row
            )
          );
        } catch (err) {
          setError(errorMessage(err));
          setRows(previous =>
            previous.map(row =>
              row.invoiceId === candidate.invoiceId
                ? { ...row, preview: undefined, selected: false, needsRefresh: true }
                : row
            )
          );
          break;
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!scope || !selected.length) return;
    setBusy(true);
    setError('');
    try {
      for (const row of selected) {
        try {
          const current = await previewCrossover({
            ...scope,
            fn: 'preview',
            invoiceId: row.invoiceId,
          });
          if (!current.eligible) {
            setRows(previous =>
              previous.map(item =>
                item.invoiceId === row.invoiceId
                  ? {
                      ...item,
                      preview: current,
                      selected: false,
                      result: 'Skipped: this claim requires manual review.',
                    }
                  : item
              )
            );
            continue;
          }
          if (
            !createDecimal(current.before.balance).isZero() ||
            !createDecimal(current.after.balance).equals(row.billedAmount)
          ) {
            throw new Error('The balance differs from the amount you confirmed.');
          }
          const result = await applyCrossover({
            ...scope,
            fn: 'apply',
            invoiceId: row.invoiceId,
            confirmed: true,
            fingerprint: current.fingerprint,
          });
          setRows(previous =>
            previous.map(item =>
              item.invoiceId === row.invoiceId
                ? {
                    ...item,
                    repaired: true,
                    selected: false,
                    result: `Updated: ${result.insertedLines} lines restored; balance ${formatMoney(result.after.balance)}.`,
                  }
                : item
            )
          );
        } catch (err) {
          const message = errorMessage(err);
          setRows(previous =>
            previous.map(item =>
              item.invoiceId === row.invoiceId
                ? {
                    ...item,
                    selected: false,
                    preview: undefined,
                    needsRefresh: true,
                    result: `Update not confirmed: ${message} Scan or preview again before retrying.`,
                  }
                : item
            )
          );
          setError(
            'Processing stopped. Earlier successful repairs are saved; remaining selected claims have not been updated.'
          );
          break;
        }
      }
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Header title="Crossover claim repair" />
      <div className="space-y-5 p-6">
        <p className="max-w-3xl text-sm text-gray-600 dark:text-dark-gray-300">
          Find forwarded claims with a zero balance and missing service lines. Select claims and
          confirm to repair them. Preview is optional; each claim is checked automatically before
          updating. Leave Clinic ID blank to list affected clinics and candidate claim counts. An
          active support grant or clinic Admin membership is required for claim details and repairs
          in production.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="text-sm">
            Clinic ID (optional)
            <input
              className={inputClass}
              value={clinicId}
              disabled={busy}
              inputMode="numeric"
              onChange={event => {
                setClinicId(event.target.value);
                if (!event.target.value.trim()) setPatientId('');
                clearResults();
              }}
            />
          </label>
          <label className="text-sm">
            Patient ID (optional)
            <input
              className={inputClass}
              value={patientId}
              disabled={busy || !clinicId.trim()}
              onChange={event => {
                setPatientId(event.target.value);
                clearResults();
              }}
            />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            text={clinicId.trim() ? 'Find affected claims' : 'List affected clinics'}
            disabled={busy}
            onClick={() => scan()}
          />
          {nextInvoiceId !== null && (
            <Button text="Load next 50" disabled={busy} onClick={() => scan(true)} />
          )}
          <Button
            text="Preview loaded claims"
            disabled={busy || !rows.some(row => !row.repaired)}
            onClick={() => preview()}
          />
          <Button
            text="Select all loaded claims"
            disabled={busy || !rows.some(canSelect)}
            onClick={() =>
              setRows(previous =>
                previous.map(row => ({
                  ...row,
                  selected: canSelect(row),
                }))
              )
            }
          />
          <Button
            text={`Review ${selected.length} selected repairs`}
            disabled={busy || !selected.length}
            onClick={() => setConfirmOpen(true)}
          />
          {busy && (
            <span role="status" className="text-sm">
              Working…
            </span>
          )}
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-700 dark:text-red-300">
            {error}
          </p>
        )}
        {clinics && (
          <div className="space-y-3">
            <p role="status" className="text-sm">
              {clinics.length} affected clinics ·{' '}
              {clinics.reduce((total, clinic) => total + clinic.affectedClaimCount, 0)} candidate
              claims. Counts include all scan candidates; each claim must pass eligibility checks
              before repair.
            </p>
            {clinics.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <caption className="sr-only">Clinics with affected crossover claims</caption>
                  <thead>
                    <tr>
                      <th className="p-3">Clinic ID</th>
                      <th className="p-3">Clinic</th>
                      <th className="p-3">Candidate claims</th>
                      <th className="p-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clinics.map(clinic => (
                      <tr
                        key={clinic.clinicId}
                        className="border-t border-gray-200 dark:border-dark-gray-700"
                      >
                        <td className="p-3">{clinic.clinicId}</td>
                        <td className="p-3">{clinic.clinicName}</td>
                        <td className="p-3">{clinic.affectedClaimCount}</td>
                        <td className="p-3">
                          <Button
                            text={`View claims for clinic ${clinic.clinicId}`}
                            disabled={busy}
                            onClick={() => {
                              setClinicId(String(clinic.clinicId));
                              scan(false, clinic.clinicId);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {hasScanned && (
          <p role="status" className="text-sm">
            {rows.length} candidate claims loaded.{' '}
            {nextInvoiceId !== null ? 'More candidates are available.' : 'Scan complete.'} Preview
            is optional. Claims that fail eligibility checks will be skipped with an explanation.
          </p>
        )}
        <div className="space-y-3">
          {rows.map(row => (
            <section
              key={row.invoiceId}
              className="rounded-lg border border-gray-200 p-4 dark:border-dark-gray-700"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center gap-3 text-sm font-medium">
                  <input
                    type="checkbox"
                    aria-label={`Select invoice ${row.invoiceId}`}
                    disabled={busy || !canSelect(row)}
                    checked={row.selected}
                    onChange={event =>
                      setRows(previous =>
                        previous.map(item =>
                          item.invoiceId === row.invoiceId
                            ? { ...item, selected: event.target.checked }
                            : item
                        )
                      )
                    }
                  />
                  Invoice {row.invoiceId} · billed {formatMoney(row.billedAmount)}
                </label>
                <Button
                  text="Preview repair"
                  disabled={busy || row.repaired}
                  onClick={() => preview(row.invoiceId)}
                />
              </div>
              {row.result && (
                <p role="status" className="mt-3 text-sm">
                  {row.result}
                </p>
              )}
              {row.preview && !row.repaired && (
                <div className="mt-3 space-y-3 text-sm">
                  {!row.preview.eligible ? (
                    <div>
                      <p className="font-medium">Manual review required</p>
                      <ul className="list-disc pl-5">
                        {row.preview.reasons.map(reason => (
                          <li key={reason}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <>
                      <p>
                        Restore {row.preview.lines.length} submission lines from invoice{' '}
                        {row.preview.sourceInvoiceId}. Status stays Submitted. Patient balances and
                        payments stay unchanged.
                      </p>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-left">
                          <thead>
                            <tr>
                              <th className="p-2">Amount</th>
                              <th className="p-2">Before</th>
                              <th className="p-2">After</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(['balance', 'estimated', 'estimatedBalance'] as const).map(field => (
                              <tr key={field}>
                                <td className="p-2">
                                  {field === 'balance'
                                    ? 'Payor balance'
                                    : field === 'estimated'
                                      ? 'Estimated amount'
                                      : 'Estimated balance'}
                                </td>
                                <td className="p-2">{formatMoney(row.preview.before[field])}</td>
                                <td className="p-2">{formatMoney(row.preview.after[field])}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <details>
                        <summary className="cursor-pointer">Service lines to restore</summary>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-left">
                            <thead>
                              <tr>
                                <th className="p-2">Code</th>
                                <th className="p-2">Modifiers</th>
                                <th className="p-2">Units</th>
                                <th className="p-2">Billed</th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.preview.lines.map(line => (
                                <tr key={line.lineItemControlNumber}>
                                  <td className="p-2">{line.code}</td>
                                  <td className="p-2">
                                    {line.modifiers.filter(Boolean).join(', ') || 'None'}
                                  </td>
                                  <td className="p-2">{line.units}</td>
                                  <td className="p-2">{formatMoney(line.billedAmount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    </>
                  )}
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
      <ConfirmModal
        isOpen={confirmOpen}
        title="Confirm crossover repairs"
        confirmText={`Update ${selected.length} claims`}
        loading={busy}
        confirmDisabled={busy || !selected.length}
        close={() => {
          if (!busy) setConfirmOpen(false);
        }}
        confirm={apply}
        description={
          <div className="space-y-3">
            <p>
              Clinic {scope?.clinicId}: restore missing lines on {selected.length} selected claims.
              The total payor balance will increase by up to {formatMoney(increase)}.
            </p>
            <p>
              Patient balances, payments, and existing adjudications will remain unchanged. Each
              claim is checked before it is updated. Ineligible claims are skipped with an
              explanation. A changed balance or request failure stops the batch.
            </p>
            <ul className="max-h-48 overflow-y-auto">
              {selected.map(row => (
                <li key={row.invoiceId}>
                  Invoice {row.invoiceId}: {formatMoney('0')} → {formatMoney(row.billedAmount)}
                </li>
              ))}
            </ul>
          </div>
        }
      />
    </>
  );
};

export default CrossoverRepair;
